package com.iregained.site.components.sections.clinicians.sections

import androidx.compose.runtime.*
import com.iregained.site.components.sections.clinicians.styles.*
import com.iregained.site.components.widgets.AnimatedHeroText
import com.iregained.site.utils.Res
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.css.TextDecorationLine
import com.varabyte.kobweb.compose.css.WhiteSpace
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.components.navigation.Link
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.animation.Keyframes
import com.varabyte.kobweb.silk.style.animation.toAnimation
import com.varabyte.kobweb.silk.style.toModifier
import kotlinx.coroutines.delay
import org.jetbrains.compose.web.css.AnimationTimingFunction
import org.jetbrains.compose.web.css.FlexWrap
import org.jetbrains.compose.web.css.percent
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.css.s
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text
import org.jetbrains.compose.web.dom.Video
import kotlin.time.Duration.Companion.milliseconds

// UNIQUE ANIMATION: Fade & Slide Down
val HeroSlideDownKeyframes = Keyframes {
    0.percent { Modifier.opacity(0).translateY((-40).px) }
    100.percent { Modifier.opacity(1).translateY(0.px) }
}

@Composable
fun CliniciansHeroSection(data: CliniciansHeroSectionData = defaultCliniciansHeroSectionData) {
    val targetId = data.sectionId?.takeIf { it.isNotBlank() } ?: "clinicians-header-section"

    // --- ANIMATION STATE ---
    val headlines = remember(data.headline, data.rotatingHeadlines) { listOf(data.headline) + data.rotatingHeadlines }
    val headlineCount = headlines.size

//    var currentIndex by remember { mutableStateOf(0) }
//    var displayedHeadline by remember { mutableStateOf(headlines.firstOrNull() ?: "") }
//    var showCursor by remember { mutableStateOf(true) }

    // Blinking cursor loop
//    LaunchedEffect(Unit) {
//        while (true) {
//            delay(500.milliseconds)
//            showCursor = !showCursor
//        }
//    }

    // Synchronized Typewriter Sequence
//    LaunchedEffect(headlineCount) {
//        if (headlineCount <= 1) return@LaunchedEffect // Nothing to rotate
//
//        while (true) {
//            val currentHeadline = headlines[currentIndex]
//
//            // 1. Hold Phase (Show full text)
//            delay(3500.milliseconds)
//
//            // 2. Delete Phase
//            for (i in currentHeadline.length downTo 0) {
//                displayedHeadline = currentHeadline.substring(0, i)
//                // OPTIMIZATION: Increased from 40ms to 60ms
//                delay(60.milliseconds)
//            }
//
//            // Brief pause while empty
//            delay(300.milliseconds)
//
//            // 3. Increment Phase
//            currentIndex = (currentIndex + 1) % headlineCount
//            val nextHeadline = headlines[currentIndex]
//
//            // 4. Reveal & Type Phase
//            for (i in 1..nextHeadline.length) {
//                displayedHeadline = nextHeadline.substring(0, i)
//                // OPTIMIZATION: Increased from 80ms to 120ms
//                delay(120.milliseconds)
//            }
//        }
//    }

    if (data.headline.isNotBlank() || data.videoPath.isNotBlank()) {
        Box(
            modifier = CliniciansHeroContainerStyle.toModifier().id(targetId)
                .styleModifier { property("scroll-margin-top", "100px") }, contentAlignment = Alignment.Center
        ) {
            Video(
                attrs = CliniciansHeroVideoStyle.toModifier().toAttrs {
                    attr("src", data.videoPath)
                    attr("poster", data.posterPath)
                    attr("playsinline", "")
                    ref { htmlVideoElement ->
                        htmlVideoElement.muted = true
                        htmlVideoElement.loop = true
                        htmlVideoElement.autoplay = true
                        htmlVideoElement.play()
                        onDispose { }
                    }
                })

            Box(modifier = CliniciansHeroOverlayStyle.toModifier())

            Column(
                modifier = CliniciansHeroContentColumnStyle.toModifier().animation(
                    HeroSlideDownKeyframes.toAnimation(
                        duration = 1.2.s, timingFunction = AnimationTimingFunction.EaseOut
                    )
                ), horizontalAlignment = Alignment.CenterHorizontally, verticalArrangement = Arrangement.Center
            ) {

                AnimatedHeroText(
                    headlines = headlines,
                    headlineModifier = CliniciansHeroHeadlineStyle.toModifier().fillMaxWidth()
                )

                // DYNAMIC TYPEWRITER HEADLINE
//                Div(
//                    attrs = CliniciansHeroHeadlineStyle.toModifier().fillMaxWidth().toAttrs()
//                ) {
//                    Text(displayedHeadline)
//                    Span(
//                        attrs = Modifier.opacity(if (showCursor) 1 else 0).toAttrs()
//                    ) {
//                        Text("|")
//                    }
//                }

                Div(
                    attrs = CliniciansHeroSubheadlineStyle.toModifier().fillMaxWidth()
                        .toAttrs { ref { htmlElement -> htmlElement.innerHTML = data.subHeadline; onDispose {} } })

                Row(
                    modifier = Modifier.gap(24.px).flexWrap(FlexWrap.Wrap),
                    verticalAlignment = Alignment.CenterVertically,
                    horizontalArrangement = Arrangement.Center
                ) {
                    Link(path = data.primaryCtaPath, modifier = Modifier.textDecorationLine(TextDecorationLine.None)) {
                        Box(modifier = CliniciansPrimaryButtonStyle.toModifier(), contentAlignment = Alignment.Center) {
                            SpanText(
                                text = data.primaryCtaText,
                                modifier = Modifier.maxWidth(200.px).fontSize(20.px).fontWeight(FontWeight.SemiBold)
                                    .whiteSpace(WhiteSpace.NoWrap)
                            )
                        }
                    }
                    Link(
                        path = data.secondaryCtaPath, modifier = Modifier.textDecorationLine(TextDecorationLine.None)
                    ) {
                        Box(
                            modifier = CliniciansSecondaryButtonStyle.toModifier(), contentAlignment = Alignment.Center
                        ) {
                            SpanText(
                                text = data.secondaryCtaText,
                                modifier = Modifier.maxWidth(200.px).fontSize(20.px).fontWeight(FontWeight.SemiBold)
                                    .whiteSpace(WhiteSpace.NoWrap)
                            )
                        }
                    }
                }
            }
        }
    }
}

// ==========================================
// CMS DATA MODEL & MEDIA SPECS
// ==========================================
/**
 * CMS VIDEO UPLOAD SPECS FOR CLINICIANS HERO:
 * @field videoPath:
 * - Aspect Ratio: 16:9 (Landscape).
 * - Recommended Dimensions: 1280x720px (720p) or 1920x1080px (1080p).
 * - Format: WebM (Highly Preferred) or MP4 (H.264).
 * - File Size: Keep under 2MB for optimal performance.
 * - Content: A 5-second seamless loop of a confident clinician analyzing data.
 * * CMS IMAGE UPLOAD SPECS FOR VIDEO POSTER:
 * @field posterPath:
 * - Aspect Ratio: 16:9 (Must exactly match the video).
 * - Recommended Dimensions: 1920x1080px.
 * - Format: WebP (Preferred) or highly compressed JPEG.
 * - File Size: MUST be under 150KB for instant Largest Contentful Paint (LCP).
 * - Content: This MUST be an export of the exact first frame of the video. If it doesn't match the first frame, users will see a jarring visual "jump" when the video finally loads and begins to play.
 */
data class CliniciansHeroSectionData(
    val sectionId: String? = null,
    val videoPath: String = Res.Videos.PatientsPage.PATIENTS_VIDEO,
    val posterPath: String = Res.Images.CliniciansPage.OVERVIEW_BG,
    val headline: String,
    val rotatingHeadlines: List<String>,
    val subHeadline: String,
    val primaryCtaText: String,
    val primaryCtaPath: String,
    val secondaryCtaText: String,
    val secondaryCtaPath: String
) {
    init {
        require(rotatingHeadlines.isNotEmpty()) { "rotatingHeadlines must not be empty" }
    }
}

val defaultCliniciansHeroSectionData = CliniciansHeroSectionData(
    // INITIAL HEADLINE
    headline = "Transformational Neurorehabilitation.",
    // ROTATING PHRASES
    rotatingHeadlines = listOf("Superior Outcomes.", "Unmatched Patient Satisfaction."),

    subHeadline = "Empower your therapists with precision and data-driven insights for superior outcomes.\n" + "The MyHand<sup>®</sup> precision mechatronic system with advanced Digital Neurotherapeutic (DTx) software seamlessly integrate into your clinical workflow for personalized therapy delivery.",
    primaryCtaText = "Request a Demo",
    primaryCtaPath = "/contact",
    secondaryCtaText = "Clinical Evidence",
    secondaryCtaPath = "/resources#resources-evidence"
)